<template>
  <div class="login-container">
    <!-- Animated grid background -->
    <div class="login-bg-grid"></div>
    <div class="login-bg-gradient"></div>

    <div class="login-box">
      <div class="login-logo-area">
        <GradientText
          text="Admin Login"
          :colors="['#48bb78', '#68d391', '#38a169', '#48bb78']"
          :animationSpeed="6"
          class="login-title"
        />
        <p class="login-subtitle">Đăng nhập để quản trị hệ thống</p>
      </div>

      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <label>Tên đăng nhập</label>
          <div class="input-wrapper">
            <i class="fas fa-user"></i>
            <input type="text" v-model="username" placeholder="Nhập tên đăng nhập" required />
          </div>
        </div>
        <div class="form-group">
          <label>Mật khẩu</label>
          <div class="input-wrapper">
            <i class="fas fa-lock"></i>
            <input type="password" v-model="password" placeholder="Nhập mật khẩu" required />
          </div>
        </div>
        <button type="submit" class="login-btn" :disabled="loading">
          <span v-if="!loading">Đăng Nhập</span>
          <span v-else class="loading-dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axiosInstance from '@/plugins/axios'
import { useRouter } from 'vue-router'
import { ToastService } from '@/services/ToastService'
import GradientText from '@/components/ui/GradientText.vue'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminLogin',
  components: { GradientText },
  setup () {
    const username = ref('')
    const password = ref('')
    const loading = ref(false)
    const router = useRouter()

    onMounted(() => {
      if (sessionStorage.getItem('session_expired_toast')) {
        sessionStorage.removeItem('session_expired_toast')
        ToastService.error('Phiên làm việc đã hết hạn, vui lòng đăng nhập lại')
      }
    })

    const handleLogin = async () => {
      try {
        loading.value = true
        const formData = new FormData()
        formData.append('username', username.value)
        formData.append('password', password.value)

        const response = await axiosInstance.post(`/admin/login`, formData)

        const { access_token, refresh_token } = response.data
        localStorage.setItem('adminToken', access_token)
        localStorage.setItem('refreshToken', refresh_token)

        router.push('/bookings')
      } catch (error) {
        ToastService.error('Đăng nhập thất bại. Vui lòng thử lại.')
        console.error('Login error:', error)
      } finally {
        loading.value = false
      }
    }

    return {
      username,
      password,
      loading,
      handleLogin
    }
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #0f1a15;
  position: relative;
  overflow: hidden;
}

/* Animated grid background */
.login-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(72, 187, 120, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(72, 187, 120, 0.06) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-drift 20s linear infinite;
}

@keyframes grid-drift {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.login-bg-gradient {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 0%, rgba(72, 187, 120, 0.12) 0%, transparent 60%),
              radial-gradient(ellipse at 80% 80%, rgba(56, 161, 105, 0.08) 0%, transparent 50%);
}

/* Glassmorphism login box */
.login-box {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(72, 187, 120, 0.15);
  border-radius: 20px;
  padding: 2.5rem;
  width: 100%;
  max-width: 420px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.login-logo-area {
  text-align: center;
  margin-bottom: 2rem;
}

.login-title {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.login-subtitle {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  font-weight: 500;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper i {
  position: absolute;
  left: 1rem;
  color: rgba(72, 187, 120, 0.5);
  font-size: 0.9rem;
}

.input-wrapper input {
  width: 100%;
  padding: 0.85rem 1rem 0.85rem 2.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  outline: none;
}

.input-wrapper input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.input-wrapper input:focus {
  border-color: rgba(72, 187, 120, 0.5);
  box-shadow: 0 0 0 3px rgba(72, 187, 120, 0.1);
  background: rgba(255, 255, 255, 0.08);
}

.login-btn {
  width: 100%;
  padding: 0.9rem;
  margin-top: 0.5rem;
  background: linear-gradient(135deg, #48bb78, #38a169);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.login-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0) 30%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0) 70%
  );
  background-size: 200% 100%;
  animation: btn-shine 3s ease-in-out infinite;
}

@keyframes btn-shine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(72, 187, 120, 0.35);
}

.login-btn:active {
  transform: translateY(0);
}

.login-btn:disabled {
  background: rgba(255, 255, 255, 0.1);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.login-btn:disabled::before {
  display: none;
}

.loading-dots span {
  animation: loading-blink 1.4s infinite both;
  font-size: 1.5rem;
  line-height: 0;
}
.loading-dots span:nth-child(2) { animation-delay: 0.2s; }
.loading-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes loading-blink {
  0%, 80%, 100% { opacity: 0; }
  40% { opacity: 1; }
}

@media (max-width: 480px) {
  .login-box {
    margin: 1rem;
    padding: 2rem 1.5rem;
  }
}
</style>
